import * as sentry from '@sentry/browser';
import apolloClient from 'services/apollo-client';
import { surfaceNodesQuery } from 'graphql/surface-node/queries/surface-nodes';
import { useCanvasClipboardOperations } from 'state/canvas-clipboard';
import { useGlobalLinkOperations } from 'state/canvas/node-linking/global-links';
import { compose, set } from 'lodash/fp';
import { SurfaceNodeType, SurfaceNodeUnion } from 'graphql/types';

export default function useRemoveNodeFromCache() {
  const { removeNodeFromClipboard } = useCanvasClipboardOperations();
  const { removeGlobalLinkByNodeId } = useGlobalLinkOperations();

  return function removeNodeFromCache(
    node: SurfaceNodeUnion,
    cache = apolloClient,
  ) {
    try {
      const surface = node?.surface?.id;

      if (surface) {
        const variables = {
          input: { filter: { surface } },
        };

        const { surfaceNodes } = cache.readQuery<any>({
          query: surfaceNodesQuery,
          variables,
        });

        removeNodeFromClipboard(node.id);
        removeGlobalLinkByNodeId(node.id);

        const nodes = deleteNodesAndLinks(surfaceNodes.nodes, [node.id]);

        cache.writeQuery({
          query: surfaceNodesQuery,
          data: {
            surfaceNodes: compose(
              set('nodes', nodes),
              set('meta', updateMeta(surfaceNodes?.meta)),
            )(surfaceNodes),
          },
          variables,
          broadcast: true,
        });
      }
    } catch (error) {
      console.error(error);
      sentry.captureException(error);
    }
  };
}

function updateMeta(meta) {
  if (meta) {
    return { ...meta, totalCount: meta.totalCount - 1, count: meta.count - 1 };
  }
  return null;
}

function deleteNodesAndLinks(nodes, idsToDelete) {
  return nodes
    .filter((node) => idsToDelete.includes(node.id) === false)
    .map((node) => ({
      ...node,
      pointsTo: node.pointsTo?.filter(
        (pointTo) => idsToDelete.includes(pointTo.to.id) === false,
      ),
    }))
    .map((node) => {
      if (node.type === SurfaceNodeType.Protocol && node.contains) {
        const containsDeletedNode = idsToDelete.some(
          (id) => id === node.contains.id,
        );

        if (containsDeletedNode) {
          return set('contains', null, node);
        }
      }
      return node;
    });
}
